import { Actions } from "@Context/common/general/context";
import { useDialogManager } from "@Context/common/general/dialog-manager-context";
import CrudActions from "@Enums/CrudActions";
import Flags from "@Enums/Flags";
import useGlobalContext from "@hooks/common/general/use-global-context";
import { useCallback, useEffect, useRef, useState } from "react";

export const BOOTCAMP_WELCOME_DIALOG = "bootcamp-welcome";

/** Opens the Bootcamp welcome once for each eligible user. */
const useBootcampWelcomeGate = () => {
  const { globalState, dispatch } = useGlobalContext();
  const { openDialog, closeDialog } = useDialogManager();
  const openedRef = useRef(false);
  const dispatchRef = useRef(dispatch);
  const [hasUnreadFlag, setHasUnreadFlag] = useState<boolean | null>(null);
  dispatchRef.current = dispatch;
  const userId = globalState.user.id;
  const isEligible = Boolean(globalState.user.authenticated && globalState.user.inBootcamp && userId);

  const completeWelcome = useCallback(() => {
    if (!userId) return;

    dispatchRef.current({
      type: Actions.UsersActions.CheckFlags,
      payload: {
        user_id: userId,
        flag_id: Flags.BOOTCAMP_WELCOME_READ,
        action: CrudActions.CREATE,
      },
      disableLoaders: true,
      onError: error => {
        console.error("Could not persist Bootcamp welcome flag", error);
      },
    });
    closeDialog(BOOTCAMP_WELCOME_DIALOG);
  }, [closeDialog, userId]);

  useEffect(() => {
    openedRef.current = false;
    setHasUnreadFlag(null);
  }, [userId]);

  useEffect(() => {
    if (!isEligible) {
      openedRef.current = false;
      setHasUnreadFlag(null);
      return;
    }

    dispatchRef.current({
      type: Actions.UsersActions.CheckFlags,
      payload: {
        user_id: userId,
        flag_id: Flags.BOOTCAMP_WELCOME_READ,
        action: CrudActions.REQUEST,
      },
      onSuccess: data => setHasUnreadFlag(data.code === "not-found"),
      onError: error => {
        console.error("Could not check Bootcamp welcome flag", error);
      },
    });
  }, [isEligible, userId]);

  useEffect(() => {
    if (!isEligible || hasUnreadFlag !== true || openedRef.current) return;

    openedRef.current = true;
    void openDialog(BOOTCAMP_WELCOME_DIALOG, { onComplete: completeWelcome }).catch(error => {
      openedRef.current = false;
      console.error("Could not open Bootcamp welcome dialog", error);
    });
  }, [completeWelcome, hasUnreadFlag, isEligible, openDialog]);
};

export default useBootcampWelcomeGate;
